import { lazy } from 'react';
import type { ComponentType, ReactElement } from 'react';
import RoleProtectedRoute from 'app/components/auth/AgentProtectedRoute';
import {
  PROTECTED_ROUTE_MANIFEST,
  type ProtectedChunkName,
  type RouteManifestEntry,
} from './RouteManifest';

export type RouteConfigEntry = {
  path: string;
  element: ReactElement;
  children?: RouteConfigEntry[];
  skipProfileFetchPath?: boolean;
};

/**
 * Protected routes. lazy() strings must be static literals — Vite cannot
 * split dynamic import() arguments. Keep in sync with sourcePath in RouteManifest.ts.
 *
 * TODO: Migrate AgentDashboard and ComplianceDashboard to layout-route + Outlet
 * so sub-routes can be individually lazy-loaded (extract layout, add children here,
 * update App.tsx to render route.children).
 */
// import() strings must be static literals — Vite cannot analyze dynamic paths.
// Keep each string in sync with the matching sourcePath in RouteManifest.ts.
const componentMap: Record<ProtectedChunkName, ComponentType> = {
  'route-agent-dashboard': lazy(() => import('app/pages/Agent/Dashboard')),
  'route-compliance-dashboard': lazy(
    () => import('app/pages/Compliance/Dashboard')
  ),
  'route-work-item-preview': lazy(
    () => import('app/modules/DailyHub/WorkItemPreviewPage')
  ),
  'route-office-admin': lazy(() => import('app/pages/OfficeAdmin')),
  'route-management': lazy(() => import('app/pages/Admin/ManagementPage')),
  'route-email-labels-auth-completed': lazy(
    () => import('app/pages/Agent/Onboarding/EmailLabelsAuthCompleted')
  ),
  'route-transaction-page': lazy(
    () => import('app/AgentDetails/TransactionPage')
  ),
  'route-configs': lazy(() => import('app/pages/Configs')),
  'route-entity-redirect': lazy(
    () => import('app/components/common/EntityRedirect')
  ),
};

export const PROTECTED_ROUTES_CONFIG: RouteConfigEntry[] =
  PROTECTED_ROUTE_MANIFEST.map((entry) => {
    const Component = componentMap[entry.chunkName];
    return {
      path: entry.routePath,
      element: <RoleProtectedRoute Component={Component} Role={entry.role} />,
      skipProfileFetchPath: (entry as RouteManifestEntry).skipProfileFetchPath,
    };
  });
